import { EncodeMedium } from '@simonbackx/simple-encoding';
import { SimpleError } from '@simonbackx/simple-errors';
import { AuditLogReplacement, AuditLogReplacementType } from '../AuditLogReplacement.js';
export class File {
    constructor(data) {
        var _a, _b, _c, _d, _e;
        /// private or not?
        this.isPrivate = false;
        /// signed url if it is a private file
        /// only filled if you have access to this file and when it has a valid signature
        this.signedUrl = null;
        /**
         * A signature that proves that this file was generated by the server and is trusted. Without a valid signature,
         * the backend won't return a signedUrl for accessing the file.
         */
        this.signature = null;
        this.id = data.id;
        this.server = data.server;
        this.path = data.path;
        this.size = data.size;
        this.name = (_a = data.name) !== null && _a !== void 0 ? _a : null;
        this.isPrivate = (_b = data.isPrivate) !== null && _b !== void 0 ? _b : false;
        this.signedUrl = (_c = data.signedUrl) !== null && _c !== void 0 ? _c : null;
        this.signature = (_d = data.signature) !== null && _d !== void 0 ? _d : null;
        this.contentType = (_e = data.contentType) !== null && _e !== void 0 ? _e : null;
    }
    getDiffValue() {
        var _a;
        return AuditLogReplacement.create({
            id: this.getPublicPath(),
            value: (_a = this.name) !== null && _a !== void 0 ? _a : undefined,
            type: AuditLogReplacementType.File,
        });
    }
    get signPayload() {
        return 'id: ' + this.id + '\n'
            + 'server: ' + this.server + '\n'
            + 'path: ' + this.path + '\n'
            + 'size: ' + this.size.toFixed(0) + '\n'
            + 'name: ' + this.name + '\n'
            + 'isPrivate: ' + (this.isPrivate ? 'true' : 'false')
            + (this.contentType ? '\ncontentType: ' + this.contentType : '');
    }
    static get signingEnabled() {
        return !!File.signFile;
    }
    async sign() {
        if (!File.signFile) {
            return false;
        }
        await File.signFile(this);
        return true;
    }
    async verify() {
        if (!this.signature) {
            return false;
        }
        if (!File.verifyFile) {
            return false;
        }
        return await File.verifyFile(this);
    }
    async withSignedUrl() {
        if (!this.signature || !this.isPrivate) {
            return null;
        }
        if (!File.getWithSignedUrl) {
            return null;
        }
        if (!await this.verify()) {
            // Never generate a signed url for an untrusted file
            return null;
        }
        return await File.getWithSignedUrl(this);
    }
    static decode(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const file = new File({
            id: data.field('id').string,
            server: data.field('server').string,
            path: data.field('path').string,
            size: data.field('size').integer,
            name: (_b = (_a = data.optionalField('name')) === null || _a === void 0 ? void 0 : _a.string) !== null && _b !== void 0 ? _b : null,
            isPrivate: (_d = (_c = data.optionalField('isPrivate')) === null || _c === void 0 ? void 0 : _c.boolean) !== null && _d !== void 0 ? _d : false,
            signedUrl: (_f = (_e = data.optionalField('signedUrl')) === null || _e === void 0 ? void 0 : _e.string) !== null && _f !== void 0 ? _f : null,
            signature: (_h = (_g = data.optionalField('signature')) === null || _g === void 0 ? void 0 : _g.string) !== null && _h !== void 0 ? _h : null,
            contentType: (_k = (_j = data.optionalField('contentType')) === null || _j === void 0 ? void 0 : _j.string) !== null && _k !== void 0 ? _k : null,
        });
        if (data.context.medium === EncodeMedium.Database || !file.isPrivate || !file.signature) {
            // Clear signed url that we read from the database - these won't be valid any longer
            file.signedUrl = null;
        }
        if (file.isPrivate && this.signingEnabled && (!data.context.medium || data.context.medium === EncodeMedium.Network)) {
            // A signature is required
            // Because of the sync nature of decoding, we cannot verify it here, but we need to do so when using the file
            if (!file.signature) {
                throw new SimpleError({
                    code: 'missing_signature',
                    message: 'Missing signature for private file',
                });
            }
        }
        return file;
    }
    encode(context) {
        return {
            id: this.id,
            server: this.server,
            path: this.path,
            size: this.size,
            name: this.name,
            isPrivate: this.isPrivate,
            signedUrl: this.isPrivate && this.signedUrl ? this.signedUrl : undefined,
            signature: this.isPrivate ? this.signature : undefined,
            contentType: this.contentType || undefined,
        };
    }
    getPublicPath() {
        if (this.signedUrl && this.isPrivate) {
            return this.signedUrl;
        }
        return this.server + '/' + this.path;
    }
    static contentTypeToExtension(contentType) {
        switch (contentType.toLocaleLowerCase()) {
            case 'image/jpeg':
            case 'image/jpg':
                return 'jpg';
            case 'image/png':
                return 'png';
            case 'image/gif':
                return 'gif';
            case 'image/webp':
                return 'webp';
            case 'image/svg+xml':
                return 'svg';
            case 'application/pdf':
                return 'pdf';
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            case 'application/vnd.ms-excel':
                return 'xlsx';
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case 'application/msword':
                return 'docx';
        }
        return null;
    }
    static removeExtension(filename) {
        return filename.split('.').slice(0, -1).join('.');
    }
    get icon() {
        var _a, _b;
        if (!this.contentType) {
            // Try based on extension
            const extension = (_b = (_a = this.name) === null || _a === void 0 ? void 0 : _a.split('.').pop()) === null || _b === void 0 ? void 0 : _b.toLowerCase();
            if (extension) {
                switch (extension) {
                    case 'png':
                    case 'jpg':
                    case 'jpeg':
                    case 'gif':
                    case 'webp':
                    case 'svg':
                        return 'file-image';
                    case 'pdf':
                        return 'file-pdf color-pdf';
                    case 'xlsx':
                    case 'xls':
                        return 'file-excel color-excel';
                    case 'docx':
                    case 'doc':
                        return 'file-word color-word';
                }
            }
            return 'file';
        }
        if (this.contentType.startsWith('image/')) {
            return 'file-image';
        }
        if (this.contentType === 'application/pdf') {
            return 'file-pdf color-pdf';
        }
        if (this.contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || this.contentType === 'application/vnd.ms-excel') {
            return 'file-excel color-excel';
        }
        if (this.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || this.contentType === 'application/msword') {
            return 'file-word color-word';
        }
        return 'file';
    }
}
/**
 * When using public/private keys inside the check functions, you can also only implement the check method
 * to verify signatures received from the backend.
 */
File.verifyFile = null;
File.signFile = null;
File.getWithSignedUrl = null;
//# sourceMappingURL=File.js.map